// src/components/layouts/app/header/desktop/LangCurrencyDropdown.tsx
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import SelectCustom from '../../../../ui/elements/SelectCustom';

interface LangOption {
  code: string;
  label: string;
  flag: string;
}

interface CurrencyOption {
  code: string;
  label: string;
  symbol: string;
}

const langOptions: LangOption[] = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
];

const currencyOptions: CurrencyOption[] = [
  { code: 'XOF', label: 'Franc CFA (XOF)', symbol: 'CFA' },
  { code: 'USD', label: 'Dollar US (USD)', symbol: '$' },
  { code: 'EUR', label: 'Euro (EUR)', symbol: '€' },
];

interface LangCurrencyDropdownProps {
  selectedLang?: string;
  selectedCurrency?: string;
  onLangChange?: (lang: string) => void;
  onCurrencyChange?: (currency: string) => void;
}

export const LangCurrencyDropdown: React.FC<LangCurrencyDropdownProps> = ({
  selectedLang = 'fr',
  selectedCurrency = 'XOF',
  onLangChange,
  onCurrencyChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | undefined>(undefined);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const currentLang = langOptions.find(l => l.code === selectedLang) || langOptions[0];
  const currentCurrency = currencyOptions.find(c => c.code === selectedCurrency) || currencyOptions[0];
  const displayLabel = `${currentLang.label} - ${currentCurrency.code}`;

  const langSelectOptions = langOptions.map(opt => ({
    value: opt.code,
    label: `${opt.flag} ${opt.label}`
  }));

  const currencySelectOptions = currencyOptions.map(opt => ({
    value: opt.code,
    label: opt.label
  }));

  return (
    <div
      className="relative"
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors duration-200"
      >
        <Globe className="w-4 h-4 text-black" />
        <span className="text-sm text-black">{displayLabel}</span>
        <ChevronDown className={`w-4 h-4 text-black transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <div
        className={`
          absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-20
          transition-all duration-300 ease-out origin-top
          ${isOpen
            ? 'opacity-100 scale-y-100 visible pointer-events-auto'
            : 'opacity-0 scale-y-0 invisible pointer-events-none'
          }
        `}
      >
        <div className="p-4 space-y-4">
          <SelectCustom
            label="Langue"
            options={langSelectOptions}
            value={selectedLang}
            onChange={(value) => onLangChange?.(value)}
            placeholder="Sélectionner une langue"
          />
          <SelectCustom
            label="Devise"
            options={currencySelectOptions}
            value={selectedCurrency}
            onChange={(value) => onCurrencyChange?.(value)}
            placeholder="Sélectionner une devise"
          />
        </div>
      </div>
    </div>
  );
};